import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { ClinicHealthPanel } from "./accountManagementClinicHealth";
import { DataCoveragePanel } from "./accountManagementDataCoverage";
import { OnboardingHealthPanel } from "./accountManagementOnboardingHealth";
import { sourceLabel } from "./accountManagementShared";
import type {
  ClinicHealthResponse,
  DataCoverageResponse,
  OnboardingHealthResponse,
} from "./accountManagementTypes";
import { DashboardsPageFrame, HorizonTabs } from "./dashboardKit";
import { accountManagementSnapshotQueryOptions } from "./dashboardsReactQuery";

function DashboardHeader({
  activeTab,
  onTabChange,
  data = null,
  clinicData = null,
  coverageData = null,
  onboardingData = null,
}: {
  activeTab: string;
  onTabChange: (value: string) => void;
  data?: { source: string; generatedAt?: string } | null;
  clinicData?: ClinicHealthResponse | null;
  coverageData?: DataCoverageResponse | null;
  onboardingData?: OnboardingHealthResponse | null;
}) {
  const clinicCount = clinicData?.summary?.clinicCount;
  const eligiblePatientCount = coverageData?.clinics.find(
    (clinic) => clinic.isPortfolioTotal,
  )?.eligiblePatientCount;
  const onboardingCount = onboardingData?.summary?.activeOnboardingCount;
  const tabs = [
    {
      value: "clinic-health",
      label: "Clinic Health",
      count: clinicCount ? `${clinicCount} clinics` : "",
    },
    {
      value: "data-coverage",
      label: "Data Coverage",
      count: eligiblePatientCount === undefined ? "" : `${eligiblePatientCount} eligible`,
    },
    {
      value: "onboarding-health",
      label: "Onboarding Health",
      count: onboardingCount === undefined ? "" : `${onboardingCount} below target`,
    },
  ];

  return (
    <header className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Account management
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Portfolio Triage
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Every clinic in your book, ranked by health — spot who needs attention, then drill in.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 text-right">
          <span
            className={
              data?.source === "live"
                ? "rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800"
                : data?.source === "mock"
                  ? "rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800"
                  : "rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground"
            }
          >
            {data ? sourceLabel(data.source) : "Loading data…"}
          </span>
          {data?.generatedAt ? (
            <small className="text-xs text-muted-foreground">
              Refreshed{" "}
              {new Date(data.generatedAt).toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
              })}
            </small>
          ) : null}
        </div>
      </div>
      <HorizonTabs
        options={tabs}
        value={activeTab}
        onChange={onTabChange}
        idPrefix="account-management"
      />
    </header>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-border/70 bg-background/80 p-6 text-sm text-muted-foreground shadow-xs">
      {label}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700 shadow-xs">
      {message}
    </div>
  );
}

const INITIAL_TAB = "clinic-health";

export function AccountManagementDashboard() {
  const [activeTab, setActiveTab] = useState(INITIAL_TAB);
  const snapshotQuery = useQuery(accountManagementSnapshotQueryOptions());
  const snapshot = snapshotQuery.data;
  const clinicData = snapshot?.clinicHealth ?? null;
  const coverageData = snapshot?.dataCoverage ?? null;
  const onboardingData = snapshot?.onboardingHealth ?? null;
  const activeData =
    activeTab === "clinic-health"
      ? clinicData
      : activeTab === "data-coverage"
        ? coverageData
        : onboardingData;

  return (
    <DashboardsPageFrame className="flex flex-col gap-8">
      <DashboardHeader
        activeTab={activeTab}
        onTabChange={setActiveTab}
        data={activeData ?? null}
        clinicData={clinicData}
        coverageData={coverageData}
        onboardingData={onboardingData}
      />
      {snapshotQuery.isLoading ? <LoadingState label="Loading portfolio triage…" /> : null}
      {snapshotQuery.error ? (
        <ErrorState
          message={
            snapshotQuery.error instanceof Error
              ? snapshotQuery.error.message
              : "Failed to load Account Management dashboard"
          }
        />
      ) : null}
      {snapshot ? (
        <>
          <section
            id="account-management-panel-clinic-health"
            role="tabpanel"
            aria-labelledby="account-management-tab-clinic-health"
            hidden={activeTab !== "clinic-health"}
          >
            <ClinicHealthPanel data={snapshot.clinicHealth} />
          </section>
          <section
            id="account-management-panel-data-coverage"
            role="tabpanel"
            aria-labelledby="account-management-tab-data-coverage"
            hidden={activeTab !== "data-coverage"}
          >
            <DataCoveragePanel data={snapshot.dataCoverage} />
          </section>
          <section
            id="account-management-panel-onboarding-health"
            role="tabpanel"
            aria-labelledby="account-management-tab-onboarding-health"
            hidden={activeTab !== "onboarding-health"}
          >
            <OnboardingHealthPanel data={snapshot.onboardingHealth} />
          </section>
        </>
      ) : null}
    </DashboardsPageFrame>
  );
}
